import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { dashboardAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  FiCalendar, FiDollarSign, FiTrendingUp, FiArrowRight,
  FiUsers, FiArrowUpRight, FiArrowDownRight, FiClock,
  FiBriefcase, FiUser, FiEye, FiFilter, FiPlus, FiUserPlus,
  FiCamera, FiVideo, FiBook
} from 'react-icons/fi';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  BarChart,
  Bar
} from 'recharts';

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const navigate = useNavigate();
  const { isCollaborator } = useAuth();

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await dashboardAPI.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const getStatusBadge = (status) => {
    const statusLower = (status || '').toLowerCase();
    if (statusLower.includes('pending') || statusLower.includes('attente') || statusLower.includes('nouvelle')) {
      return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-300">En attente</Badge>;
    }
    if (statusLower.includes('approved') || statusLower.includes('confirm') || statusLower.includes('accept') || statusLower.includes('valid')) {
      return <Badge className="bg-green-100 text-green-800 border-green-300">Acompte payé</Badge>;
    }
    if (statusLower.includes('completed') || statusLower.includes('terminée')) {
      return <Badge className="bg-blue-100 text-blue-800 border-blue-300">Terminée</Badge>;
    }
    if (statusLower.includes('cancel') || statusLower.includes('annul')) {
      return <Badge className="bg-red-100 text-red-800 border-red-300">Annulée</Badge>;
    }
    return <Badge>{status}</Badge>;
  };

  const getMachineIcon = (type) => {
    const typeLower = (type || '').toLowerCase();
    if (typeLower.includes('photobooth') || typeLower.includes('photo')) {
      return <FiCamera className="w-5 h-5 text-purple-600" />;
    }
    if (typeLower.includes('video') || typeLower.includes('360')) {
      return <FiVideo className="w-5 h-5 text-blue-600" />;
    }
    if (typeLower.includes('livre') || typeLower.includes('book')) {
      return <FiBook className="w-5 h-5 text-amber-600" />;
    }
    return <FiCamera className="w-5 h-5 text-gray-600" />;
  };

  const getMachineColor = (type) => {
    const typeLower = (type || '').toLowerCase();
    if (typeLower.includes('photobooth') || typeLower.includes('photo')) {
      return { bg: 'bg-purple-50', text: 'text-purple-600', bar: '#8b5cf6' };
    }
    if (typeLower.includes('video') || typeLower.includes('360')) {
      return { bg: 'bg-blue-50', text: 'text-blue-600', bar: '#3b82f6' };
    }
    if (typeLower.includes('livre') || typeLower.includes('book')) {
      return { bg: 'bg-amber-50', text: 'text-amber-600', bar: '#d4af37' };
    }
    return { bg: 'bg-gray-50', text: 'text-gray-600', bar: '#6b7280' };
  };

  // Custom tooltip for the chart
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white border border-gray-200 shadow-lg rounded-lg p-3">
          <p className="font-semibold text-gray-900 mb-2">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }} className="text-sm">
              {entry.name}: {formatCurrency(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="dashboard-loading">
        <div className="h-8 w-48 bg-gray-200 rounded animate-pulse" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-32 bg-gray-200 rounded-xl animate-pulse" />
          ))}
        </div>
        <div className="h-80 bg-gray-200 rounded-xl animate-pulse" />
      </div>
    );
  }

  const momGrowth = stats?.revenue?.mom_growth || 0;
  const isGrowthPositive = momGrowth >= 0;
  const chartData = stats?.revenue?.chart || [];
  const machineStats = stats?.machines || [];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isCollaborator ? 'Mes Réservations' : 'Tableau de bord'}
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            {isCollaborator ? 'Réservations qui vous sont assignées' : 'Vue d\'ensemble de votre activité'}
          </p>
        </div>
        {!isCollaborator && (
          <div className="flex gap-2">
            <Link to="/dashboard/reservations/new">
              <Button size="sm" className="bg-[#d4af37] hover:bg-[#c4a030] text-black">
                <FiPlus className="w-4 h-4 mr-2" />
                Créer réservation
              </Button>
            </Link>
            <Link to="/dashboard/customers/new">
              <Button variant="outline" size="sm">
                <FiUserPlus className="w-4 h-4 mr-2" />
                Créer client
              </Button>
            </Link>
          </div>
        )}
      </div>

      {/* COLLABORATOR VIEW - Simple reservations list only */}
      {isCollaborator ? (
        <div className="space-y-4">
          {/* Quick Actions */}
          <div className="flex gap-3">
            <Link to="/dashboard/reservations" className="flex-1">
              <Card className="hover:shadow-md transition-shadow cursor-pointer border-2 border-transparent hover:border-indigo-200">
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="p-3 bg-indigo-100 rounded-xl">
                    <FiCalendar className="w-6 h-6 text-indigo-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Voir mes réservations</p>
                    <p className="text-sm text-gray-500">Liste complète des réservations assignées</p>
                  </div>
                  <FiArrowRight className="ml-auto text-gray-400" />
                </CardContent>
              </Card>
            </Link>
            <Link to="/dashboard/planning" className="flex-1">
              <Card className="hover:shadow-md transition-shadow cursor-pointer border-2 border-transparent hover:border-green-200">
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="p-3 bg-green-100 rounded-xl">
                    <FiClock className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Planning</p>
                    <p className="text-sm text-gray-500">Calendrier des événements</p>
                  </div>
                  <FiArrowRight className="ml-auto text-gray-400" />
                </CardContent>
              </Card>
            </Link>
          </div>

          {/* Assigned Reservations List */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <FiCalendar className="w-4 h-4 text-indigo-500" />
                  Réservations à venir
                </CardTitle>
                <Link to="/dashboard/reservations">
                  <Button variant="ghost" size="sm" className="text-indigo-600">
                    Voir tout <FiArrowRight className="ml-1 w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              {stats?.reservations?.pending_list?.length > 0 ? (
                <div className="space-y-2">
                  {stats.reservations.pending_list.slice(0, 5).map((reservation, idx) => (
                    <motion.div
                      key={reservation.id}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.05 }}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
                      onClick={() => navigate(`/dashboard/reservations/${reservation.id}`)}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-semibold text-sm">
                          {reservation.client_name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{reservation.client_name}</p>
                          <p className="text-xs text-gray-500">
                            {reservation.product_name} • {formatDate(reservation.event_date)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {getStatusBadge(reservation.status)}
                        <FiEye className="w-4 h-4 text-gray-400" />
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-400">
                  <FiCalendar className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="font-medium">Aucune réservation assignée</p>
                  <p className="text-sm mt-1">Contactez l'administrateur pour être assigné à des réservations</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      ) : (
        <>
          {/* ADMIN VIEW - Full dashboard with stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* CA Total */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Card className="bg-gradient-to-br from-[#1a1a2e] to-[#2d2d44] text-white border-0 overflow-hidden">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div className="p-2 bg-white/10 rounded-lg">
                  <FiDollarSign className="w-5 h-5" />
                </div>
                <div className={`flex items-center gap-1 text-xs ${isGrowthPositive ? 'text-green-400' : 'text-red-400'}`}>
                  {isGrowthPositive ? <FiArrowUpRight /> : <FiArrowDownRight />}
                  {Math.abs(momGrowth)}%
                </div>
              </div>
              <div className="mt-4">
                <p className="text-2xl font-bold">{formatCurrency(stats?.revenue?.total)}</p>
                <p className="text-sm text-white/60 mt-1">CA Total</p>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* CA Mois en cours */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.05 }}
        >
          <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white border-0 overflow-hidden">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div className="p-2 bg-white/10 rounded-lg">
                  <FiTrendingUp className="w-5 h-5" />
                </div>
                <span className="text-xs text-white/80">Ce mois</span>
              </div>
              <div className="mt-4">
                <p className="text-2xl font-bold">{formatCurrency(stats?.revenue?.current_month)}</p>
                <p className="text-sm text-white/60 mt-1">vs {formatCurrency(stats?.revenue?.last_month)} mois dernier</p>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Réservations */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
        >
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0 overflow-hidden">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div className="p-2 bg-white/10 rounded-lg">
                  <FiCalendar className="w-5 h-5" />
                </div>
                <Link to="/dashboard/reservations?status=pending">
                  <Badge className="bg-white/20 text-white border-0 hover:bg-white/30 cursor-pointer">
                    {stats?.reservations?.pending || 0} en attente
                  </Badge>
                </Link>
              </div>
              <div className="mt-4">
                <p className="text-2xl font-bold">{stats?.reservations?.total || 0}</p>
                <p className="text-sm text-white/60 mt-1">Réservations totales</p>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Acomptes encaissés */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.15 }}
        >
          <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white border-0 overflow-hidden">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div className="p-2 bg-white/10 rounded-lg">
                  <FiClock className="w-5 h-5" />
                </div>
              </div>
              <div className="mt-4">
                <p className="text-2xl font-bold">{formatCurrency(stats?.revenue?.deposits)}</p>
                <p className="text-sm text-white/60 mt-1">Acomptes encaissés</p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Revenue Chart */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.2 }}
      >
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <FiTrendingUp className="w-4 h-4 text-gray-500" />
              Évolution du CA
            </CardTitle>
          </CardHeader>
          <CardContent>
            {chartData.length > 0 ? (
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    data={chartData}
                    margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                  >
                    <defs>
                      <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#d4af37" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#d4af37" stopOpacity={0.1}/>
                      </linearGradient>
                      <linearGradient id="colorEntreprise" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.1}/>
                      </linearGradient>
                      <linearGradient id="colorParticulier" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.1}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis 
                      dataKey="month" 
                      tick={{ fontSize: 12, fill: '#6b7280' }}
                      tickLine={false}
                      axisLine={{ stroke: '#e5e7eb' }}
                    />
                    <YAxis 
                      tick={{ fontSize: 12, fill: '#6b7280' }}
                      tickLine={false}
                      axisLine={{ stroke: '#e5e7eb' }}
                      tickFormatter={(value) => `${value}€`}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend 
                      verticalAlign="top" 
                      height={36}
                      formatter={(value) => <span className="text-sm text-gray-600">{value}</span>}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="revenue" 
                      name="CA Total"
                      stroke="#d4af37" 
                      strokeWidth={2}
                      fillOpacity={1} 
                      fill="url(#colorRevenue)" 
                    />
                    <Area 
                      type="monotone" 
                      dataKey="entreprise" 
                      name="Entreprises"
                      stroke="#3b82f6" 
                      strokeWidth={2}
                      fillOpacity={0.6} 
                      fill="url(#colorEntreprise)" 
                    />
                    <Area 
                      type="monotone" 
                      dataKey="particulier" 
                      name="Particuliers"
                      stroke="#8b5cf6" 
                      strokeWidth={2}
                      fillOpacity={0.6} 
                      fill="url(#colorParticulier)" 
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-80 flex items-center justify-center text-gray-400">
                <div className="text-center">
                  <FiTrendingUp className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>Pas encore de données pour afficher le graphique</p>
                  <p className="text-sm mt-1">Les données apparaîtront une fois les premières réservations acceptées</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Client Type Breakdown & Machine Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Répartition par type de client */}
        <Card className="lg:col-span-1">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <FiUsers className="w-4 h-4 text-gray-500" />
              Répartition Clients
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Entreprises */}
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <FiBriefcase className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Entreprises</p>
                    <p className="text-xs text-gray-500">{stats?.clients?.entreprise?.count || 0} réservations</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-bold text-blue-600">{formatCurrency(stats?.clients?.entreprise?.revenue)}</p>
                  <p className="text-xs text-gray-500">{stats?.clients?.entreprise?.percentage || 0}%</p>
                </div>
              </div>

              {/* Particuliers */}
              <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <FiUser className="w-4 h-4 text-purple-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Particuliers</p>
                    <p className="text-xs text-gray-500">{stats?.clients?.particulier?.count || 0} réservations</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-bold text-purple-600">{formatCurrency(stats?.clients?.particulier?.revenue)}</p>
                  <p className="text-xs text-gray-500">{stats?.clients?.particulier?.percentage || 0}%</p>
                </div>
              </div>

              {/* Visual bar */}
              <div className="h-3 bg-gray-100 rounded-full overflow-hidden flex">
                <div 
                  className="bg-blue-500 transition-all duration-500"
                  style={{ width: `${stats?.clients?.entreprise?.percentage || 0}%` }}
                />
                <div 
                  className="bg-purple-500 transition-all duration-500"
                  style={{ width: `${stats?.clients?.particulier?.percentage || 0}%` }}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Répartition par type de machine */}
        <Card className="lg:col-span-1">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <FiCamera className="w-4 h-4 text-gray-500" />
              Réservations par Machine
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {machineStats.length > 0 ? (
                machineStats.map((machine, index) => {
                  const colors = getMachineColor(machine.type);
                  return (
                    <div key={index} className={`flex items-center justify-between p-3 ${colors.bg} rounded-lg`}>
                      <div className="flex items-center gap-3">
                        <div className={`p-2 ${colors.bg} rounded-lg`}>
                          {getMachineIcon(machine.type)}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{machine.type}</p>
                          <p className="text-xs text-gray-500">{machine.count} réservations</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`font-bold ${colors.text}`}>{formatCurrency(machine.revenue)}</p>
                        <p className="text-xs text-gray-500">{machine.percentage}%</p>
                      </div>
                    </div>
                  );
                })
              ) : (
                <>
                  {/* Default machine types */}
                  <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-purple-100 rounded-lg">
                        <FiCamera className="w-4 h-4 text-purple-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Photobooth</p>
                        <p className="text-xs text-gray-500">0 réservations</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-purple-600">0€</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <FiVideo className="w-4 h-4 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">VideoBooth 360°</p>
                        <p className="text-xs text-gray-500">0 réservations</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-blue-600">0€</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-amber-100 rounded-lg">
                        <FiBook className="w-4 h-4 text-amber-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Livre d'Or Vidéo</p>
                        <p className="text-xs text-gray-500">0 réservations</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-amber-600">0€</p>
                    </div>
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Réservations en attente */}
        <Card className="lg:col-span-1">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <FiClock className="w-4 h-4 text-gray-500" />
                Réservations
              </CardTitle>
              <div className="flex items-center gap-2">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[120px] h-8 text-xs" data-testid="status-filter">
                    <FiFilter className="w-3 h-3 mr-1" />
                    <SelectValue placeholder="Filtrer" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">En attente</SelectItem>
                    <SelectItem value="approved">Acceptées</SelectItem>
                    <SelectItem value="all">Toutes</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {stats?.reservations?.pending_list?.length > 0 ? (
              <div className="space-y-2 max-h-[260px] overflow-y-auto">
                {stats.reservations.pending_list
                  .filter(r => {
                    if (statusFilter === 'all') return true;
                    const status = (r.status || '').toLowerCase();
                    if (statusFilter === 'pending') {
                      return status.includes('pending') || status.includes('attente') || status.includes('nouvelle');
                    }
                    if (statusFilter === 'approved') {
                      return status.includes('approved') || status.includes('confirm') || status.includes('accept');
                    }
                    return true;
                  })
                  .slice(0, 5)
                  .map((reservation, index) => (
                  <motion.div
                    key={reservation.id}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="flex items-center justify-between p-2 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer group"
                    onClick={() => navigate(`/dashboard/reservations/${reservation.id}`)}
                    data-testid={`reservation-${reservation.id}`}
                  >
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <div className={`w-2 h-2 rounded-full ${
                        reservation.client_type === 'entreprise' ? 'bg-blue-500' : 'bg-purple-500'
                      }`} />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate text-sm">{reservation.client_name}</p>
                        <p className="text-xs text-gray-500 truncate">
                          {formatDate(reservation.event_date)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <p className="font-semibold text-gray-900 text-sm">{formatCurrency(reservation.total_price)}</p>
                      <FiEye className="w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100" />
                    </div>
                  </motion.div>
                ))}
                <Link to={`/dashboard/reservations?status=${statusFilter}`} className="block">
                  <Button variant="ghost" size="sm" className="w-full text-xs mt-2">
                    Voir tout <FiArrowRight className="w-3 h-3 ml-1" />
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="text-center py-6 text-gray-500">
                <FiCalendar className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                <p className="text-sm">Aucune réservation</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Link to="/dashboard/reservations?status=approved">
          <Card className="hover:shadow-md transition-shadow cursor-pointer">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <FiCalendar className="w-4 h-4 text-green-600" />
                </div>
                <div>
                  <p className="text-xl font-bold text-gray-900">{stats?.reservations?.approved || 0}</p>
                  <p className="text-xs text-gray-500">Acompte payé</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>
        
        <Link to="/dashboard/reservations?status=completed">
          <Card className="hover:shadow-md transition-shadow cursor-pointer">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <FiCalendar className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <p className="text-xl font-bold text-gray-900">{stats?.reservations?.completed || 0}</p>
                  <p className="text-xs text-gray-500">Terminées</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to="/dashboard/customers">
          <Card className="hover:shadow-md transition-shadow cursor-pointer">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <FiUsers className="w-4 h-4 text-purple-600" />
                </div>
                <div>
                  <p className="text-xl font-bold text-gray-900">{stats?.customers_count || 0}</p>
                  <p className="text-xs text-gray-500">Clients</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to="/dashboard/products-formulas">
          <Card className="hover:shadow-md transition-shadow cursor-pointer">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <FiTrendingUp className="w-4 h-4 text-amber-600" />
                </div>
                <div>
                  <p className="text-xl font-bold text-gray-900">{stats?.products_count || 0}</p>
                  <p className="text-xs text-gray-500">Produits</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>
      </div>
        </>
      )}
    </div>
  );
}
